import type { SourcePostFilters } from '@/hooks/useSourcePosts';
import { daneMiekkieOferty, stanWarunku, type PoleMiekkie } from '@/lib/filtryMiekkie';
import { obnizkaCeny } from '@/lib/obnizkaCeny';
import {
  KOLEJNOSC_WSTAZKI_KARTY as KOLEJNOSC_WSTAZKI_KARTY_WSPOLNA,
  wyroznikWstazki as wyroznikWstazkiWspolny,
  wyroznikWstazkiKarty as wyroznikWstazkiKartyWspolny,
} from '@/lib/serpRead/wstazka';

/**
 * WYRÓŻNIKI OFERTY — jedno źródło dla trzech powierzchni (decyzje Piotra 30.09,
 * docs/handoff/2026-09-30-wyrozniki-kandydaci.md):
 *  1. wstążka na zdjęciu — najwyżej jedna, na karcie i na stronie oferty według tej samej
 *     `KOLEJNOSC_WSTAZKI_KARTY` (Piotr 7.10; „Przyjazne zwierzętom” już nie na wstążce);
 *  2. karta w wynikach — reszta w pastylkach (najwyżej 2 + „+N” z dymkiem listy schowanych);
 *  3. strona oferty — pełna lista w sekcji „Co wyróżnia to mieszkanie”, każdy ze źródłem.
 *
 * KONTEKST (filtr — wariant 2): cecha, której najemca szuka w filtrach, idzie na początek listy z
 * „pasuje do Twojego wyszukiwania” i do PASTYLEK z ✓, nie na wstążkę; wstążka bierze najwyższy
 * dozwolony wyróżnik spoza filtrów. Cechy częstsze niż próg 25% domyślnego SERP (winda, balkon,
 * umeblowane…) wyróżnikiem są TYLKO w kontekście — same z siebie nie wyróżniają.
 *
 * Pewność: O — właściciel na FlatHero; P — ogłoszenie (parser); R — wykryte regułą w opisie;
 * W — wyliczone przez nas. „Taniej” i „po remoncie” liczy projekcja (migracja 20260930114353).
 */

export type Pewnosc = 'O' | 'P' | 'R' | 'W';
export type IdWyroznika =
  | 'zweryfikowany' | 'wlasciciel' | 'zwierzeta' | 'tanio' | 'obnizka' | 'media_w_cenie' | 'garaz_w_cenie' | 'parking_w_cenie' | 'nowe' | 'taras' | 'ogrod' | 'garaz'
  | 'klima' | 'remont_wlasciciela' | 'remont' | 'nowe_budownictwo' | 'swiatlowod' | 'biurko'
  // tylko w kontekście (udział > 25% domyślnego SERP)
  | 'od_zaraz' | 'zmywarka' | 'winda' | 'metro' | 'parking' | 'balkon' | 'umeblowane';

export interface Wyroznik {
  id: IdWyroznika;
  pewnosc: Pewnosc;
  priorytet: number;
  /** Najemca ma tę cechę w filtrach — idzie na początek z „pasuje do Twojego wyszukiwania”. */
  pasuje: boolean;
}

export interface OfertaDoWyroznikow {
  owner_confirmed?: boolean | null;
  owner_present?: boolean | null;
  posted_at?: string | null;
  metro_nearest_m?: number | null;
  extracted_extras?: unknown;
  has_elevator?: boolean | null;
  amenities_norm?: string[] | null;
  deposit_pln?: number | null;
  po_remoncie?: boolean | null;
  taniej_o_procent?: number | null;
  /** Obniżka ceny (migracja 20261007081536) i cena, przy której ją porównujemy. */
  rent_reduced_from_pln?: number | null;
  rent_reduced_at?: string | null;
  extracted_rent_pln?: number | null;
}

export interface KontekstWyroznikow {
  filtry?: SourcePostFilters | null;
  teraz?: number;
}

/** Próg „to nie wyróżnik” — udział na domyślnym SERP (decyzja Piotra 30.09: „na ten moment tak”). */
export const PROG_POWSZECHNOSCI = 0.25;
/**
 * WSTĄŻKA NA ZDJĘCIU, karta w wynikach, strona oferty i e-mail z alertem: jedna, pierwsza z tej
 * listy, którą oferta ma (Piotr 7.10). Stała mieszka w `serpRead/wstazka.ts` (wspólna z
 * `send-filter-alerts`); zmiana kolejności albo zestawu = zmiana tam. Celowo poza listą:
 * „Parking w cenie” i „Przyjazne zwierzętom” (tylko pastylka / lista cech).
 */
export const KOLEJNOSC_WSTAZKI_KARTY: readonly IdWyroznika[] = KOLEJNOSC_WSTAZKI_KARTY_WSPOLNA;
/** „Nowe” — tyle godzin. */
export const GODZIN_NOWEJ = 24;

interface Pozycja {
  id: IdWyroznika;
  pewnosc: Pewnosc;
  priorytet: number;
  tylkoKontekst?: boolean;
  /** Pole filtra miękkiego — dopasowanie z `stanWarunku` (ta sama definicja co kreska listy). */
  pole?: PoleMiekkie;
  /** Filtr spoza filtrów miękkich (metro, od zaraz). */
  filtr?: (f: SourcePostFilters) => boolean;
}

/** Katalog w kolejności priorytetu (docs/handoff/2026-09-30-wyrozniki-kandydaci.md). */
export const KATALOG_WYROZNIKOW: readonly Pozycja[] = [
  { id: 'zweryfikowany', pewnosc: 'O', priorytet: 1 },
  { id: 'wlasciciel', pewnosc: 'O', priorytet: 2 },
  { id: 'zwierzeta', pewnosc: 'P', priorytet: 3, pole: 'zwierzeta' },
  { id: 'tanio', pewnosc: 'W', priorytet: 4 },
  // Obniżka ceny (Piotr 7.10): z historii postów tego mieszkania, liczona przez bazę.
  { id: 'obnizka', pewnosc: 'W', priorytet: 4 },
  // Opłaty w cenie (Piotr 7.10): zaraz po „taniej”, bo też mówią o rachunku najemcy.
  { id: 'media_w_cenie', pewnosc: 'P', priorytet: 4 },
  { id: 'garaz_w_cenie', pewnosc: 'P', priorytet: 4, pole: 'udogodnienie.parking' },
  { id: 'parking_w_cenie', pewnosc: 'P', priorytet: 4, pole: 'udogodnienie.parking' },
  { id: 'nowe', pewnosc: 'W', priorytet: 5 },
  { id: 'taras', pewnosc: 'P', priorytet: 6 },
  { id: 'ogrod', pewnosc: 'P', priorytet: 6, pole: 'udogodnienie.garden' },
  { id: 'garaz', pewnosc: 'P', priorytet: 7 },
  { id: 'klima', pewnosc: 'P', priorytet: 7, pole: 'udogodnienie.air_conditioning' },
  { id: 'remont_wlasciciela', pewnosc: 'O', priorytet: 8 },
  { id: 'remont', pewnosc: 'R', priorytet: 8 },
  { id: 'nowe_budownictwo', pewnosc: 'P', priorytet: 8 },
  { id: 'swiatlowod', pewnosc: 'P', priorytet: 9 },
  { id: 'biurko', pewnosc: 'P', priorytet: 9, pole: 'udogodnienie.desk_home_office' },
  { id: 'od_zaraz', pewnosc: 'P', priorytet: 20, tylkoKontekst: true, filtr: (f) => !!f.availableNow },
  { id: 'zmywarka', pewnosc: 'P', priorytet: 21, tylkoKontekst: true, pole: 'udogodnienie.dishwasher' },
  { id: 'winda', pewnosc: 'P', priorytet: 22, tylkoKontekst: true, pole: 'winda' },
  { id: 'metro', pewnosc: 'W', priorytet: 23, tylkoKontekst: true, filtr: (f) => !!f.nearMetro },
  { id: 'parking', pewnosc: 'P', priorytet: 24, tylkoKontekst: true, pole: 'udogodnienie.parking' },
  { id: 'balkon', pewnosc: 'P', priorytet: 25, tylkoKontekst: true, pole: 'balkon' },
  { id: 'umeblowane', pewnosc: 'P', priorytet: 26, tylkoKontekst: true, pole: 'umeblowanie' },
];

/**
 * „Po remoncie” ZE ŹRÓDŁA WŁAŚCICIELA (decyzja Piotra 30.09). Osobny wyróżnik od `remont`,
 * który wykrywa regułą w opisie ogłoszenia scrapowanego — tu właściciel odpowiedział na
 * konkretne pytanie w kreatorze, więc pewność jest O, a dymek mówi, CO podał.
 *
 * PRÓG: kuchnia ALBO łazienka = „do 3 lat”. Nie jest to gust: nasza własna etykieta dla tej
 * wartości brzmi „po remoncie (do 3 lat)”, a pozostałe dwie to „remont 3–5 lat temu” i „remont
 * ponad 5 lat temu” — czyli formularz, w którym właściciel odpowiadał, nazywa „po remoncie”
 * wyłącznie tę jedną odpowiedź, a pozostałe traktuje jako stwierdzenie WIEKU.
 *
 * MALOWANIE SAMO NIE WYSTARCZA. Świeża farba nie jest remontem; gdyby wystarczała, wyróżnik
 * pojawiałby się na mieszkaniach bez żadnego remontu i przestałby cokolwiek znaczyć. Malowanie
 * tylko DOPOWIADA w dymku, gdy wyróżnik i tak się należy.
 *
 * Brak odpowiedzi to brak wyróżnika — trzy stany, nie dwa. `null` nigdy nie udaje „nie”.
 *
 * Wartości czytamy z `extras` warstwy serwującej (`flat.listing_effective_extras` wkłada je tam
 * na ramieniu właściciela, a `flat.serving_extras` ich nie zdejmuje), więc nie ma tu sklejania
 * przy odczycie — Konwencja 26.
 */
export interface SzczegolRemontuWlasciciela {
  kuchnia: boolean;
  lazienka: boolean;
  /** Malowanie „do roku” — tylko dopowiedzenie, nigdy powód wyróżnika. */
  malowanie: boolean;
}

const REMONT_SWIEZY = 'within_3y';
const MALOWANIE_SWIEZE = 'within_1y';

/** `null`, gdy wyróżnik się NIE należy (także gdy właściciel nie odpowiedział). */
export function szczegolRemontuWlasciciela(
  e: Record<string, unknown>,
): SzczegolRemontuWlasciciela | null {
  const kuchnia = e.kitchen_renovation === REMONT_SWIEZY;
  const lazienka = e.bathroom_renovation === REMONT_SWIEZY;
  if (!kuchnia && !lazienka) return null;
  return { kuchnia, lazienka, malowanie: e.painting_renovation === MALOWANIE_SWIEZE };
}

/** Wygodna nakładka dla miejsc wywołania: bierze ofertę, nie znormalizowane extras, więc
 *  wywołujący nie musi znać `daneMiekkieOferty` ani pamiętać, żeby ją wywołać. */
export function remontWlascicielaOferty(o: OfertaDoWyroznikow): SzczegolRemontuWlasciciela | null {
  return szczegolRemontuWlasciciela(daneMiekkieOferty(o));
}

function ma(id: IdWyroznika, o: OfertaDoWyroznikow, e: Record<string, unknown>, teraz: number): boolean {
  const a = Array.isArray(e.amenities) ? (e.amenities as unknown[]) : [];
  switch (id) {
    case 'zweryfikowany': return o.owner_confirmed === true;
    // Szczebel niżej niż zweryfikowany — nigdy oba naraz.
    case 'wlasciciel': return o.owner_confirmed !== true && o.owner_present === true;
    // „Do uzgodnienia” pasuje do najemcy ze zwierzęciem, ale bez wyróżnika (Piotr 30.09).
    case 'zwierzeta': return e.pets_allowed === true && e.pet_terms_negotiable !== true;
    case 'tanio': return o.taniej_o_procent != null && o.taniej_o_procent > 0;
    case 'obnizka': return obnizkaCeny(o, teraz) !== null;
    // Pola jak w dopiskach kosztów (src/lib/dopiskiCeny.ts); `false` i brak = brak wyróżnika.
    case 'media_w_cenie': return e.media_included === true;
    case 'garaz_w_cenie': return e.parking_included === true && e.parking_type === 'underground';
    case 'parking_w_cenie': return e.parking_included === true && e.parking_type !== 'underground';
    case 'nowe': return !!o.posted_at && teraz - new Date(o.posted_at).getTime() < GODZIN_NOWEJ * 3600e3;
    // Surowe `extras.amenities` (ścieżka tabeli) bywa po polsku — „taras” widziany w danych 30.09.
    case 'taras': return a.includes('terrace') || a.includes('taras');
    case 'ogrod': return a.includes('garden');
    // Miejsce w cenie mówi więcej niż sam garaż/parking — wtedy tylko wersja „w cenie”.
    case 'garaz': return a.includes('garage') && e.parking_included !== true;
    case 'klima': return a.includes('air_conditioning');
    case 'remont_wlasciciela': return szczegolRemontuWlasciciela(e) !== null;
    // Szczebel niżej niż wersja właściciela — nigdy oba naraz (ten sam wzorzec co
    // zweryfikowany/właściciel). Deklaracja człowieka bije regułę na tekście.
    case 'remont': return o.po_remoncie === true && szczegolRemontuWlasciciela(e) === null;
    case 'nowe_budownictwo': return e.building_age === 'new_post_2015';
    case 'swiatlowod': return a.includes('fibre_internet');
    case 'biurko': return a.includes('desk_home_office');
    case 'od_zaraz': return e.available_immediately === true;
    case 'zmywarka': return a.includes('dishwasher');
    case 'winda': return e.has_elevator === true;
    case 'metro': return o.metro_nearest_m != null && o.metro_nearest_m <= 800;
    case 'parking': return (a.includes('parking') || a.includes('garage')) && e.parking_included !== true;
    case 'balkon': return a.includes('balcony') || a.includes('terrace');
    case 'umeblowane': return e.furnishing === 'furnished' || e.furnishing === 'partially';
  }
}

/**
 * Pełna lista wyróżników oferty: najpierw te, których najemca szuka (`pasuje`), potem priorytet.
 * Cecha „tylko kontekst” wchodzi wyłącznie, gdy najemca jej szuka I oferta ją ma.
 */
export function wyroznikiOferty(oferta: OfertaDoWyroznikow, kontekst: KontekstWyroznikow = {}): Wyroznik[] {
  const teraz = kontekst.teraz ?? Date.now();
  const e = daneMiekkieOferty(oferta);
  const f = kontekst.filtry ?? {};
  const wynik: Wyroznik[] = [];
  for (const k of KATALOG_WYROZNIKOW) {
    const jest = ma(k.id, oferta, e, teraz);
    if (!jest) continue;
    const pasuje = k.pole ? stanWarunku(k.pole, f, e) === 'spelnia' : k.filtr ? k.filtr(f) : false;
    if (k.tylkoKontekst && !pasuje) continue;
    wynik.push({ id: k.id, pewnosc: k.pewnosc, priorytet: k.priorytet, pasuje });
  }
  return wynik.sort((a, b) => Number(b.pasuje) - Number(a.pasuje) || a.priorytet - b.priorytet);
}


/**
 * Wstążka: JEDNA na zdjęciu (Piotr 30.09: „ograniczyć ribbon do 1”), pierwsza z
 * `KOLEJNOSC_WSTAZKI_KARTY` (Piotr 7.10), ta sama na karcie i na stronie oferty. Cecha z filtra
 * nie idzie na wstążkę (wariant 2: jest w pastylkach z ✓). Na karcie reszta trafia do pastylek
 * pod ceną (`pastylkiKarty`), bez tej z wstążki.
 */
export function wyroznikWstazki(lista: readonly Wyroznik[]): Wyroznik | null {
  return wyroznikWstazkiWspolny(lista);
}

/**
 * Wstążka KARTY w wynikach — od 7.10 to samo co `wyroznikWstazki` (zaufanie stoi na początku
 * `KOLEJNOSC_WSTAZKI_KARTY`). Ta sama funkcja wybiera wstążkę w e-mailach z alertami
 * (`send-filter-alerts`, plan §1.68).
 */
export function wyroznikWstazkiKarty(lista: readonly Wyroznik[]): Wyroznik | null {
  return wyroznikWstazkiKartyWspolny(lista);
}

/**
 * Na karcie w pastylkach NIE MA: tego, co jest na wstążce; zaufania (karta ma własną plakietkę
 * drabiny zaufania); „nowe” (karta pisze „około N godzin temu”).
 */
const POZA_PASTYLKAMI_KARTY = new Set<IdWyroznika>(['zweryfikowany', 'wlasciciel', 'nowe']);

export interface PastylkiKarty {
  widoczne: Wyroznik[];
  /** Ile schowanych za „+N”. */
  ukryte: number;
  /** Schowane za „+N”, w kolejności listy (dymek „+N” je wymienia). */
  schowane: Wyroznik[];
}

/**
 * Karta: najwyżej 2 pastylki + „+N”. Do 30.09 przy trzech pokazywaliśmy wszystkie, ale w siatce
 * trzech kolumn na pulpicie (karta ~245 px) trzecia ucinała wszystkie do „Przyjazne z…”, „Świat…”
 * (zrzut z 30.09) — dwie całe czytają się lepiej niż trzy ucięte.
 */
export function pastylkiKarty(lista: readonly Wyroznik[], naWstazce: Wyroznik | null): PastylkiKarty {
  const reszta = lista.filter((w) => w !== naWstazce && !POZA_PASTYLKAMI_KARTY.has(w.id));
  const widoczne = reszta.slice(0, 2);
  return { widoczne, ukryte: reszta.length - widoczne.length, schowane: reszta.slice(2) };
}

export interface PastylkaMapy {
  /** Jedyna pastylka karty nad mapą; `null` = karta bez wyróżników. */
  pierwsza: Wyroznik | null;
  /** Ile schowanych za „+N”. */
  ukryte: number;
  /** Schowane za „+N”. */
  schowane: Wyroznik[];
}

/**
 * KARTA NAD MAPĄ (wariant B, 30.09): miniatura ma 112 px — wstążka łamała się tam na dwie linie i
 * zasłaniała zdjęcie, a druga pastylka nie mieści się na 320 px. Dlatego JEDNA pastylka + „+N”:
 *  - cecha z filtra (wariant 2 — ta sama zasada co na liście) ma pierwszeństwo, z ✓;
 *  - inaczej cecha wstążkowa karty (`KOLEJNOSC_WSTAZKI_KARTY`), rysowana w kolorze wstążki;
 *  - inaczej pierwszy wyróżnik z listy.
 * Poza pastylką i „+N”, jak na liście: zaufanie (karta ma własną linię) i „nowe” (data).
 */
export function pastylkaMapy(lista: readonly Wyroznik[]): PastylkaMapy {
  const dozwolone = lista.filter((w) => !POZA_PASTYLKAMI_KARTY.has(w.id));
  const pierwsza = dozwolone.find((w) => w.pasuje) ?? wyroznikWstazki(dozwolone) ?? dozwolone[0] ?? null;
  const schowane = pierwsza ? dozwolone.filter((w) => w !== pierwsza) : [];
  return { pierwsza, ukryte: schowane.length, schowane };
}

/**
 * Rok remontu do dymka „Po remoncie” — z frazy czasu przepisanej przez model z opisu
 * (`remont_kiedy`: „w 2025 roku”, „2023”, „świeżo”, „2 lata temu”). Tylko jawny rok 1950–2099;
 * frazy względnej nie przeliczamy na rok (nie znamy daty, od której liczy wystawca).
 */
export function rokRemontu(kiedy: string | null | undefined): string | null {
  const m = String(kiedy ?? '').match(/(?<!\d)(19[5-9]\d|20\d{2})(?!\d)/);
  return m ? m[1] : null;
}
